import React, { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router'
import { useDispatch } from 'react-redux'
import WelcomeTemplate from './templates/WelcomeTemplate'
import kycCore, { checkLbpToken } from '../services/Kyc'
import { ROUTES } from '../enums/routes'
import { loadingActions } from '../store/slices/Loading'
import { shouldDeleteDocumentsActions } from '../store/slices/DeleteDocuments'

interface Props {
  phoneNumber: string
}

const TwoFactorAuth: FC<Props> = ({ phoneNumber }) => {
  const { register, handleSubmit, watch } = useForm()
  const { token: kyc_token } = useParams()
  const [error, setError] = useState()
  const [isLoading, setLoading] = useState(false)
  const history = useHistory()
  const dispatch = useDispatch()

  const onSubmit = handleSubmit(async ({ auth_code }) => {
    setLoading(true)
    const loginData = await kycCore.request.request({
      endpoint: 'api/2fa/login',
      method: 'POST',
      data: {
        kyc_token,
        auth_code,
        strategy: 'louvre',
      },
    })

    const loginToken = await loginData.json()

    if (loginData.status) {
      setLoading(false)
    }

    if (loginData.status === 400) {
      setError('Le code saisi est incorrect')
      return
    }

    if (loginData.status === 429) {
      setError(
        'Votre compte a été temporairement verrouillé. Veuillez réessayer dans 10 minutes'
      )
      return
    }

    if (loginToken.login === 'success') {
      dispatch(loadingActions.loading())
      kycCore.auth.login(loginToken.token)
      dispatch(loadingActions.loaded())
      dispatch(shouldDeleteDocumentsActions.setShouldDeleteDocuments(true))
      history.push(ROUTES.home)
    }
  })

  const disabled =
    !watch('auth_code') || watch('auth_code').length !== 6 || isLoading

  const [isDisabled, setIsDisabled] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (isDisabled && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1)
      }, 1000)
    } else if (timeLeft === 0) {
      setIsDisabled(false)
    }

    return () => {
      if (timer) {
        clearInterval(timer)
      }
    }
  }, [isDisabled, timeLeft])

  const handleClick = async () => {
    setIsDisabled(true)
    setTimeLeft(30)
    await checkLbpToken(kyc_token)
  }

  return (
    <WelcomeTemplate>
      <div className="twoFactorCardWrapper">
        <div className="card cardContent px15 py6 px6md mb3 mb5md mb0md">
          <h2 className="fontBold textCenter mb4">
            Veuillez vous connecter pour accéder à votre espace
          </h2>
          <div className="px15 py2 twoFactorCard">
            <h3 className="mb15">Identification par SMS</h3>
            <p className="mb15">
              Veuillez saisir le code qui vous a été envoyé par SMS sur votre
              numéro de téléphone {phoneNumber}
            </p>

            <form onSubmit={onSubmit}>
              <label htmlFor="auth_code" className={error ? 'textDanger' : ''}>
                <span className="dBlock">Code</span>
                <input
                  type="password"
                  name="auth_code"
                  id="auth_code"
                  onChange={() => setError('')}
                  placeholder="Saisissez le code reçu"
                  className={`twoFactorInput ${
                    error ? 'twoFactorInputError' : ''
                  }`}
                  ref={register({ required: true })}
                />
              </label>
              <p className="textDanger">{error}</p>
              <button
                type="submit"
                disabled={disabled}
                className="btn mt35 twoFactorBtn"
              >
                Se connecter
              </button>
            </form>
            <button
              type="button"
              className="twoFactorBtnResend"
              onClick={handleClick}
              disabled={isDisabled || isLoading}
            >
              Recevoir un nouveau code
            </button>
          </div>
        </div>
      </div>
    </WelcomeTemplate>
  )
}

export default TwoFactorAuth
